/**
 * Sub-spec 09 Plan 6 — /settings/integrations/microchip
 *
 * Toggle providers + paste API keys per provider. API keys are encrypted
 * server-side before persistence; the GET returns only a mask + a boolean
 * indicating whether a key is set.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type ProviderSlug = 'petlog' | 'identibase' | 'aaha_universal';

interface ProviderRow {
  slug: ProviderSlug;
  apiKeyMasked: string | null;
  apiKeySet: boolean;
}

interface ProvidersResponse {
  default: ProviderSlug | null;
  providers: ProviderRow[];
}

const PROVIDER_LABELS: Record<ProviderSlug, { name: string; needsKey: boolean; markets: string }> =
  {
    petlog: { name: 'Petlog', needsKey: true, markets: 'United Kingdom' },
    identibase: { name: 'Identibase', needsKey: true, markets: 'United Kingdom' },
    aaha_universal: {
      name: 'AAHA Universal',
      needsKey: false,
      markets: 'United States, Canada (no API key needed)',
    },
  };

const ALL_SLUGS: ProviderSlug[] = ['petlog', 'identibase', 'aaha_universal'];

export function MicrochipSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<Record<ProviderSlug, boolean>>({
    petlog: false,
    identibase: false,
    aaha_universal: false,
  });
  const [apiKeys, setApiKeys] = useState<Record<ProviderSlug, string>>({
    petlog: '',
    identibase: '',
    aaha_universal: '',
  });
  const [keyAlreadySet, setKeyAlreadySet] = useState<Record<ProviderSlug, boolean>>({
    petlog: false,
    identibase: false,
    aaha_universal: false,
  });
  const [defaultSlug, setDefaultSlug] = useState<ProviderSlug | ''>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/microchip/providers', { credentials: 'include' });
      if (!res.ok) {
        setError('Unable to load microchip providers.');
        return;
      }
      const body = (await res.json()) as ProvidersResponse;
      const nextEnabled: Record<ProviderSlug, boolean> = {
        petlog: false,
        identibase: false,
        aaha_universal: false,
      };
      const nextSet: Record<ProviderSlug, boolean> = {
        petlog: false,
        identibase: false,
        aaha_universal: false,
      };
      for (const p of body.providers) {
        nextEnabled[p.slug] = true;
        nextSet[p.slug] = p.apiKeySet;
      }
      setEnabled(nextEnabled);
      setKeyAlreadySet(nextSet);
      setDefaultSlug(body.default ?? '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const providers = ALL_SLUGS.filter((s) => enabled[s]).map((slug) => ({
        slug,
        apiKey: apiKeys[slug] ? apiKeys[slug] : null,
      }));
      const body: { providers: typeof providers; default?: ProviderSlug } = { providers };
      if (defaultSlug && enabled[defaultSlug]) body.default = defaultSlug;
      const res = await fetch('/api/microchip/providers', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string; issues?: string[] };
        setError(err?.issues?.join(', ') ?? err?.error ?? 'Save failed.');
        return;
      }
      // Clear paste fields after save so the next save doesn't re-submit keys.
      setApiKeys({ petlog: '', identibase: '', aaha_universal: '' });
      await load();
    } finally {
      setSaving(false);
    }
  }, [enabled, apiKeys, defaultSlug, load]);

  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h1 className="text-xl font-semibold">Microchip lookup</h1>
        <p className="text-sm text-muted-foreground">
          Configure providers used by the "Lookup chip" action on pet detail pages. The platform
          tries providers in order, returning the first match.
        </p>
      </header>

      {error ? (
        <div className="rounded-md bg-warning/10 border border-warning px-3 py-2 text-sm text-warning">
          {error}
        </div>
      ) : null}

      <ul className="space-y-3">
        {ALL_SLUGS.map((slug) => {
          const info = PROVIDER_LABELS[slug];
          return (
            <li key={slug} className="rounded-lg border border-border bg-card p-4 space-y-2">
              <label className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-sm">{info.name}</div>
                  <div className="text-xs text-muted-foreground">{info.markets}</div>
                </div>
                <input
                  type="checkbox"
                  checked={enabled[slug]}
                  onChange={(e) => setEnabled((prev) => ({ ...prev, [slug]: e.target.checked }))}
                />
              </label>
              {enabled[slug] && info.needsKey ? (
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-muted-foreground">API key</label>
                  <input
                    type="password"
                    autoComplete="off"
                    placeholder={
                      keyAlreadySet[slug] ? 'Saved — paste new key to replace' : 'Paste API key'
                    }
                    value={apiKeys[slug]}
                    onChange={(e) => setApiKeys((prev) => ({ ...prev, [slug]: e.target.value }))}
                    className="w-full text-sm rounded-md border border-border px-3 py-2"
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div>
        <label className="block text-sm font-medium mb-1">Default provider</label>
        <select
          value={defaultSlug}
          onChange={(e) => setDefaultSlug(e.target.value as ProviderSlug | '')}
          className="text-sm rounded-md border border-border px-3 py-2"
        >
          <option value="">No default — try in order</option>
          {ALL_SLUGS.filter((s) => enabled[s]).map((s) => (
            <option key={s} value={s}>
              {PROVIDER_LABELS[s].name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <Link to="/settings/integrations" className="text-sm underline">
          Back to integrations
        </Link>
      </div>
    </div>
  );
}
